/*
 * Site search: the suggestion dropdown under every search box, and spelling correction.
 *
 * Suggestions come from Shopify's own predictive search (/search/suggest.json), which is
 * already forgiving of most slips ("lanterms", "nano lopes"). When it still finds nothing
 * ("josef koet"), the query is corrected against the words that actually occur in the
 * catalogue (product titles, artists, product types) and searched again. The same
 * correction runs on the results page when a search comes back empty.
 *
 * The dropdown reuses Boost's class names; its styling is assets/dg-search-suggest.css
 * (and assets/dg-catalog-base.css on collection and search pages).
 */
(function () {
  'use strict';

  // Loaded from the layout and from the collection/search sections; run once.
  if (window.DGSearch) return;

  var INPUTS = 'form[action$="/search"] input[name="q"]';
  var HIDE_PRICE_VENDOR = 'Josef Kote'; // click-for-price artist: no price anywhere on a card
  var VOCAB_KEY = 'dg_search_vocab_v1';
  var VOCAB_TTL = 24 * 60 * 60 * 1000;

  var box = null;        // the dropdown element
  var activeInput = null;
  var lastQuery = '';
  var timer = null;
  var seq = 0;

  function money(amount) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(amount); }
    catch (e) { return '$' + Number(amount).toFixed(2); }
  }
  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }
  function sized(url, w) { return url ? url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w : ''; }

  /* ---------- spelling correction ---------- */

  // Optimal string alignment distance (insert, delete, substitute, swap neighbours),
  // abandoned early once it must exceed `max`.
  function distance(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    var prev2 = null, prev = [], cur, i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur = [i];
      var rowMin = i;
      for (j = 1; j <= lb; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (prev2 && i > 1 && j > 1 && a.charCodeAt(i - 1) === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev;
      prev = cur;
    }
    return prev[lb];
  }

  function tokens(text) {
    return String(text).toLowerCase().replace(/[^a-z0-9\s'-]/g, ' ').split(/[\s-]+/)
      .map(function (w) { return w.replace(/^'+|'+$/g, ''); })
      .filter(function (w) { return w.length > 1; });
  }

  // Returns the corrected query, or null when nothing needed changing.
  function correct(query, vocab) {
    var changed = false;
    var out = tokens(query).map(function (word) {
      if (word.length < 3 || /^\d+$/.test(word) || vocab.set[word]) return word;
      var max = word.length <= 4 ? 1 : word.length <= 8 ? 2 : 3;
      var best = null, bestScore = Infinity;
      for (var i = 0; i < vocab.list.length; i++) {
        var cand = vocab.list[i];
        var d = distance(word, cand, max);
        if (d > max) continue;
        // Prefer a closer word; among equals, one that starts the same way, then a commoner one.
        var score = d * 100 + (cand.charAt(0) === word.charAt(0) ? 0 : 30) - Math.min(20, vocab.set[cand]);
        if (score < bestScore) { bestScore = score; best = cand; }
      }
      if (best) { changed = true; return best; }
      return word;
    });
    return changed ? out.join(' ') : null;
  }

  var vocabPromise = null;
  function loadVocab() {
    if (vocabPromise) return vocabPromise;
    try {
      var cached = JSON.parse(localStorage.getItem(VOCAB_KEY) || 'null');
      if (cached && Date.now() - cached.t < VOCAB_TTL) {
        vocabPromise = Promise.resolve(toVocab(cached.counts));
        return vocabPromise;
      }
    } catch (e) {}
    var counts = {};
    function add(text, weight) {
      tokens(text).forEach(function (w) { if (w.length > 2 && !/^\d+$/.test(w)) counts[w] = (counts[w] || 0) + weight; });
    }
    // One page at a time: the storefront rate-limits bursts.
    function page(n) {
      return fetch('/collections/all?view=dg-vocab&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (text) {
          var data = JSON.parse(text);
          (data.words || []).forEach(function (w) { add(w, 1); });
          (data.vendors || []).forEach(function (v) { add(v, 20); });
          (data.types || []).forEach(function (v) { add(v, 20); });
          if (n < data.pages && n < 12) return page(n + 1);
        });
    }
    vocabPromise = page(1).then(function () {
      try { localStorage.setItem(VOCAB_KEY, JSON.stringify({ t: Date.now(), counts: counts })); } catch (e) {}
      return toVocab(counts);
    }).catch(function () { vocabPromise = null; return toVocab(counts); });
    return vocabPromise;
  }
  function toVocab(counts) { return { set: counts, list: Object.keys(counts) }; }

  /* ---------- suggestions ---------- */

  function suggest(q) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources%5Btype%5D=product,collection&resources%5Blimit%5D=4&resources%5Blimit_scope%5D=each&resources%5Boptions%5D%5Bunavailable_products%5D=last';
    return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (d) {
      var res = (d.resources && d.resources.results) || {};
      return { products: res.products || [], collections: res.collections || [] };
    });
  }

  function highlight(title, q) {
    var words = tokens(q);
    if (!words.length) return esc(title);
    var re = new RegExp('(' + words.map(function (w) { return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('|') + ')', 'ig');
    return esc(title).replace(re, '<b>$1</b>');
  }

  function searchUrl(q) {
    return '/search?type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(q);
  }

  function render(q, data, correctedFrom) {
    var html = '';
    if (correctedFrom) {
      html += '<div class="dg-suggest-note">Showing results for <a href="' + searchUrl(q) + '"><b>' + esc(q) + '</b></a></div>';
    }
    if (data.collections.length) {
      html += '<div class="boost-pfs-search-suggestion-groups-others"><div class="boost-pfs-search-suggestion-group" data-group="collections" aria-label="Collections"><ul>' +
        '<li class="boost-pfs-search-suggestion-header-collections boost-pfs-search-suggestion-header" aria-label="Collections">Collections</li>';
      data.collections.forEach(function (c) {
        html += '<li class="boost-pfs-search-suggestion-item boost-pfs-ui-item" role="option"><a tabindex="-1" href="' + esc(c.url) + '">' + esc(c.title) + '</a></li>';
      });
      html += '</ul></div></div>';
    }
    if (data.products.length) {
      html += '<div class="boost-pfs-search-suggestion-groups-pro"><div class="boost-pfs-search-suggestion-group" data-group="products" aria-label="Products"><ul>' +
        '<li class="boost-pfs-search-suggestion-header-products boost-pfs-search-suggestion-header" aria-label="Products">Products</li>';
      data.products.forEach(function (p) {
        var img = (p.featured_image && p.featured_image.url) || p.image;
        var price = '';
        if (p.vendor !== HIDE_PRICE_VENDOR) {
          var lo = parseFloat(p.price_min || p.price), hi = parseFloat(p.price_max || p.price);
          if (!isNaN(lo)) price = hi > lo ? money(lo) + ' - ' + money(hi) : money(lo);
        }
        html += '<li class="boost-pfs-search-suggestion-item boost-pfs-search-suggestion-item-product boost-pfs-ui-item" role="option"><a tabindex="-1" href="' + esc(p.url.split('?')[0]) + '">' +
          '<div class="boost-pfs-search-suggestion-left">' + (img ? '<img tabindex="-1" src="' + esc(sized(img, 200)) + '" alt="' + esc(p.title) + '" loading="lazy">' : '') + '</div>' +
          '<div class="boost-pfs-search-suggestion-right"><p class="boost-pfs-search-suggestion-product-title">' + highlight(p.title, q) + '</p>' +
          (price ? '<p class="boost-pfs-search-suggestion-product-price"><span class="boost-pfs-search-suggestion-product-regular-price">' + price + '</span></p>' : '') +
          '</div></a></li>';
      });
      html += '</ul></div></div>';
    }
    if (!data.products.length && !data.collections.length) {
      html += '<div class="dg-suggest-note">No results for <b>' + esc(q) + '</b></div>';
    }
    return html;
  }

  function ensureBox() {
    if (box) return box;
    box = document.createElement('div');
    box.className = 'dg-suggest boost-pfs-search-suggestion-wrapper boost-pfs-search-suggestion-column-2-non-fullwidth boost-pfs-search-suggestion-product-position-right boost-pfs-search-suggestion-product-item-type-list boost-pfs-search-suggestion-products-per-row-2';
    box.setAttribute('role', 'listbox');
    box.innerHTML = '<div class="boost-pfs-search-suggestion-popover" data-direction="right" style="top: -20px; right: 20px;"></div><div tabindex="-1" class="boost-pfs-search-suggestion"></div>';
    document.body.appendChild(box);
    return box;
  }

  function place() {
    if (!box || !activeInput) return;
    var r = activeInput.getBoundingClientRect();
    var vw = document.documentElement.clientWidth;
    var narrow = vw < 768;
    // Tablets keep the two-column layout but span the window, like phones do.
    var fit = !narrow && vw < 1025;
    box.classList.toggle('dg-suggest--narrow', narrow);
    box.classList.toggle('dg-suggest--fit', fit);
    box.style.top = (r.bottom + 12) + 'px';
    if (narrow || fit) {
      box.style.left = '10px';
      box.style.right = '10px';
    } else {
      box.style.left = 'auto';
      box.style.right = Math.max(10, vw - r.right) + 'px';
    }
    var inner = box.querySelector('.boost-pfs-search-suggestion');
    inner.style.maxHeight = Math.max(200, window.innerHeight - r.bottom - 40) + 'px';
  }

  function open() {
    ensureBox();
    place();
    box.classList.add('boost-pfs-search-suggestion-open');
    if (activeInput) activeInput.setAttribute('aria-expanded', 'true');
  }
  function close() {
    if (!box) return;
    box.classList.remove('boost-pfs-search-suggestion-open');
    if (activeInput) activeInput.setAttribute('aria-expanded', 'false');
  }

  function run(q) {
    var mine = ++seq;
    suggest(q).then(function (data) {
      if (mine !== seq) return;
      if (data.products.length || data.collections.length) return show(q, data, null);
      // Nothing found. Say so at once, then try the corrected spelling.
      show(q, data, null);
      return loadVocab().then(function (vocab) {
        var fixed = correct(q, vocab);
        if (!fixed || mine !== seq) return;
        return suggest(fixed).then(function (again) {
          if (mine !== seq) return;
          if (again.products.length || again.collections.length) show(fixed, again, q);
        });
      });
    }).catch(function () {});
  }

  function show(q, data, correctedFrom) {
    ensureBox();
    box.querySelector('.boost-pfs-search-suggestion').innerHTML = render(q, data, correctedFrom);
    open();
  }

  function onInput(input) {
    activeInput = input;
    var q = input.value.trim();
    clearTimeout(timer);
    if (q.length < 2) { seq++; lastQuery = ''; close(); return; }
    if (q === lastQuery && box && box.classList.contains('boost-pfs-search-suggestion-open')) return;
    lastQuery = q;
    timer = setTimeout(function () { run(q); }, 180);
  }

  function items() { return box ? Array.prototype.slice.call(box.querySelectorAll('.boost-pfs-search-suggestion-item')) : []; }
  function moveSelection(dir) {
    var list = items();
    if (!list.length) return;
    var idx = list.findIndex(function (li) { return li.classList.contains('selected'); });
    if (idx > -1) list[idx].classList.remove('selected');
    idx = idx + dir;
    if (idx < 0) idx = list.length - 1;
    if (idx >= list.length) idx = 0;
    list[idx].classList.add('selected');
    list[idx].scrollIntoView({ block: 'nearest' });
  }

  /* ---------- wiring ----------
   * Listeners sit on the document, so search boxes added later (the menu drawer) work too.
   */
  function isSearchInput(el) { return el && el.matches && el.matches(INPUTS); }

  ['input', 'keyup', 'focusin', 'focus', 'click'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (!isSearchInput(e.target)) return;
      if (type === 'input' || type === 'focusin') onInput(e.target);
      // Fetch the word list while the shopper is still typing, so a correction is instant.
      if (type === 'focusin') loadVocab();
    }, true);
  });

  document.addEventListener('keydown', function (e) {
    if (!isSearchInput(e.target)) return;
    var isOpen = box && box.classList.contains('boost-pfs-search-suggestion-open');
    if (e.key === 'Escape') { close(); return; }
    if (!isOpen) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); moveSelection(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveSelection(-1); }
    else if (e.key === 'Enter') {
      var sel = box.querySelector('.boost-pfs-search-suggestion-item.selected a');
      if (sel) { e.preventDefault(); e.stopPropagation(); location.href = sel.href; }
    }
  }, true);

  document.addEventListener('click', function (e) {
    if (!box || box.contains(e.target) || isSearchInput(e.target)) return;
    close();
  });
  window.addEventListener('resize', place);
  window.addEventListener('scroll', place, { passive: true });

  // Every search box searches products, and matches the last word as a prefix ("giraff").
  function prepareForms() {
    Array.prototype.slice.call(document.querySelectorAll('form[action$="/search"]')).forEach(function (form) {
      if (form.getAttribute('data-dg-search')) return;
      form.setAttribute('data-dg-search', '1');
      [['type', 'product'], ['options[prefix]', 'last']].forEach(function (pair) {
        var existing = form.querySelector('input[name="' + pair[0] + '"]');
        if (existing) { existing.value = pair[1]; return; }
        var hidden = document.createElement('input');
        hidden.type = 'hidden'; hidden.name = pair[0]; hidden.value = pair[1];
        form.appendChild(hidden);
      });
      var q = form.querySelector('input[name="q"]');
      if (q) { q.setAttribute('autocomplete', 'off'); q.setAttribute('role', 'combobox'); q.setAttribute('aria-autocomplete', 'list'); q.setAttribute('aria-expanded', 'false'); }
    });
  }

  /* ---------- results page: empty search ----------
   * A search that found nothing is retried once with corrected spelling. `dgq` carries
   * what was originally typed, so the page can say so and so it never loops.
   */
  function resultsPage() {
    var grid = document.querySelector('[data-dg-grid][data-dg-context="search"]');
    if (!grid) return;
    var params = new URLSearchParams(location.search);
    var original = params.get('dgq');
    var note = document.querySelector('[data-dg-corrected]');
    if (original && note) {
      note.textContent = 'No results for "' + original + '". Showing results for "' + (grid.getAttribute('data-terms') || '') + '" instead.';
      note.hidden = false;
      return;
    }
    if (grid.getAttribute('data-count') !== '0' || !grid.getAttribute('data-terms')) return;
    var typed = params.get('q') || '';
    loadVocab().then(function (vocab) {
      var fixed = correct(typed, vocab);
      if (!fixed) return;
      suggest(fixed).then(function (data) {
        if (!data.products.length) return;
        params.set('q', fixed);
        params.set('dgq', typed);
        params.delete('page');
        location.replace(location.pathname + '?' + params.toString());
      });
    });
  }

  function init() { prepareForms(); resultsPage(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', prepareForms);

  // Exposed for the runnable check in scripts/dg-search-check.cjs and for debugging.
  window.DGSearch = { correct: correct, distance: distance, tokens: tokens, loadVocab: loadVocab };
})();
